import translate from '@allesauto/i18n';
import { DropdownOpenHeight } from '@allesauto/utils/consts';
import { isAuregoShop } from '@allesauto/utils/funcs.ts';
import { ChevronDown20, Close20 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import Select, {
  type GroupBase,
  type SelectInstance,
  components,
} from 'react-select';

import './SearchDropdown.css';

import Checkbox from '../checkbox';
import { CircularLoaderSize } from '../circularLoader';
import InfoIconBox from '../infoIconBox';
import LoaderOverlay from '../loaderOverlay';
import { useWhiteLabel } from '../whiteLabel';

export interface Option {
  value: string | number;
  label: string | number;
  isDisabled?: boolean;
}

export interface OptionsGroup {
  label: string;
  options: Option[];
}

export interface SearchDropdownProps {
  id?: string;
  options?: Option[] | OptionsGroup[] | null;
  darkBg?: boolean;
  disabled?: boolean;
  className?: string;
  closeOnSelect?: boolean;
  isMulti?: boolean;
  label?: string;
  placeholder?: string;
  fieldRequired?: boolean;
  hasGreyBg?: boolean;
  hasInfoIcon?: boolean;
  hasLabelAnimation?: boolean;
  infoText?: string;
  headline?: string;
  headlineRedOnError?: boolean;
  context?: string;
  isClearable?: boolean;
  isSearchable?: boolean;
  openHeight?: DropdownOpenHeight;
  headlinePosition?: 'left' | 'top';
  value: Option | readonly Option[] | null;
  inputValue?: string | undefined;
  onChange?: (value: Option | readonly Option[] | null) => void;
  onInputChange?: (value: any) => void;
  shouldSort?: boolean;
  hideDropdownIndicator?: boolean;
  hideNoOptions?: boolean;
  errorMessage?: string;
  isIdentificationForm?: boolean;
  isLoading?: boolean;
  tradeInWidget?: boolean;
  customDataString?: string;
}

const sortOptions = (
  option1: Option | OptionsGroup,
  option2: Option | OptionsGroup
) => {
  if (option1.label < option2.label) return -1;
  if (option1.label > option2.label) return 1;
  return 0;
};

const multiValueElement = (props: any) => {
  if (!props.hasValue) return null;

  const selectedValues = props?.selectProps?.value || [];
  let val = props.data.label;
  const isLastElement =
    selectedValues[selectedValues.length - 1].label === props.data.label;

  if (selectedValues.length > 1 && !isLastElement) val += ', ';

  return val;
};

// react-select does not render serverside
// to avoid a layout shift this skeleton which mimics the React selects markup is rendered on SSR
const SearchDropdownSkeleton = (disabled?: boolean, placeholder?: string) => (
  <div className="react-select-container vehicle-search-form">
    <div
      className={`react-select__control ${
        disabled ? 'react-select__control--is-disabled ' : ''
      }`}
    >
      <div className="react-select__value-container">
        <div className="react-select__placeholder">{placeholder ?? ''}</div>
      </div>
      <div className="react-select__indicators">
        <div className="react-select__dropdown-indicator react-select__indicator">
          <ChevronDown20 aria-hidden="true" />
        </div>
      </div>
    </div>
  </div>
);

const SearchDropdown = ({
  id,
  options,
  disabled,
  isMulti,
  darkBg = false,
  label,
  className: customClassName,
  placeholder = '',
  hasGreyBg = false,
  hasInfoIcon = false,
  hasLabelAnimation = true,
  infoText = '',
  headline = '',
  headlinePosition = 'top',
  headlineRedOnError = false,
  isClearable = true,
  isSearchable = true,
  openHeight = DropdownOpenHeight.MAX,
  onChange,
  fieldRequired = false,
  value,
  inputValue,
  context = '',
  onInputChange,
  shouldSort = false,
  hideDropdownIndicator = false,
  hideNoOptions = false,
  errorMessage,
  isIdentificationForm = false,
  isLoading = false,
  tradeInWidget = false,
  customDataString,
}: SearchDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOptionSelected, setIsOptionSelected] = useState(!(value == null));
  const [hasMounted, setHasMounted] = useState(false);
  const whiteLabel = useWhiteLabel();
  const intl = useIntl();

  const overrideSelect = useCallback(
    (select: SelectInstance<Option, boolean, GroupBase<Option>> | null) => {
      if (select) {
        select.stopListeningToTouch();
        select.onTouchEnd = function (event: TouchEvent) {
          if (this.userIsDragging) return;

          // close the menu if the user taps outside
          // we're checking on event.target here instead of event.currentTarget, because we want to assert information
          // on events on child elements, not the document (which we've attached this handler to).
          if (
            this.controlRef &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            !this.controlRef.contains(event.target) &&
            this.menuListRef &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            !this.menuListRef.contains(event.target) &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            !this.controlRef.contains(event.composedPath()[0]) &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            !this.menuListRef.contains(event.composedPath()[0])
          ) {
            this.blurInput();
          }

          // reset move vars
          this.initialTouchX = 0;
          this.initialTouchY = 0;
        };

        // re-attach the listeners since onTouchEnd was replaced
        select.startListeningToTouch();
      }
    },
    []
  );

  useEffect(() => {
    setIsOptionSelected(!(value == null));
  }, [value]);

  useEffect(() => {
    // Fix issue with SSR (by only rendering during CSR)
    // See https://github.com/JedWatson/react-select/issues/3590#issuecomment-1345592394
    setHasMounted(true);
  }, []);

  const sortedOptions = useMemo(() => {
    if (!shouldSort) return options;

    const sortedOptions = (options && [...options].sort(sortOptions)) || [];

    for (const option of sortedOptions) {
      if (!('options' in option)) break;
      option.options.sort(sortOptions);
    }

    return sortedOptions;
  }, [options, shouldSort]);

  const className = {
    dropdownWrapper: clsx(
      'flex',
      openHeight + '-dropdown',
      headlinePosition === 'left' && 'flex-row items-center',
      headlinePosition === 'top' && 'flex-col',
      (context.includes('range-dropdown--min') ||
        context.includes('range-dropdown--max')) &&
        'flex-1'
    ),
    headlineWrapper: clsx(
      'flex flex-none items-center mb-5px',
      headlinePosition === 'left' && 'mr-20px !mb-0'
    ),
    headlineStyle: clsx(
      headlinePosition === 'left' && 'mb-0',
      hasInfoIcon && 'mr-[11px]',
      disabled && darkBg && 'text-white',
      darkBg && 'text-white',
      !darkBg && 'text-grey80',
      headlineRedOnError && errorMessage && !isOpen && '!text-red60',
      fieldRequired && !isOptionSelected && !isOpen && 'text-red60'
    ),
    labelStyle: clsx(
      'absolute duration-300 left-15px max-w-[calc(100%-30px)] pointer-events-none px-5px text-p1 top-15px truncate z-40',
      !darkBg && fieldRequired && !isOptionSelected && !isOpen && 'text-red60',
      darkBg && fieldRequired && !isOptionSelected && !isOpen && 'text-white',
      disabled && darkBg && 'text-functional05 monochrome:text-grey60',
      disabled && !darkBg && 'text-grey50 monochrome:text-grey60',
      darkBg &&
        'bg-purple80 text-white monochrome:bg-brandColor monochrome:text-grey10',
      !darkBg &&
        'text-purple80 bg-white monochrome:bg-white monochrome:text-black',
      hasGreyBg && 'bg-grey10 monochrome:bg-grey10',
      isOpen && hasLabelAnimation && 'label-animation',
      value && hasLabelAnimation && 'label-animation',
      isOptionSelected && !isMulti && hasLabelAnimation && 'label-animation',
      isMulti &&
        Array.isArray(value) &&
        value.length &&
        hasLabelAnimation &&
        'label-animation',
      !hasLabelAnimation &&
        isOptionSelected &&
        !isMulti &&
        'label-animation--hidden',
      isMulti &&
        Array.isArray(value) &&
        value.length &&
        !hasLabelAnimation &&
        'label-animation--hidden',
      isSearchable && isOpen && !hasLabelAnimation && 'label-animation--hidden',
      tradeInWidget && 'text-black'
    ),
    arrow: clsx(isOpen && 'rotate-180', tradeInWidget ? 'black' : ''),
    optionWrapper: clsx(
      '!border-orange-600 !cursor-pointer !m-0 !px-20px !py-10px relative select-none',
      'hover:!bg-green60',
      'monochrome:hover:!bg-grey30',
      tradeInWidget && 'hover:!bg-grey10',
      darkBg &&
        '!text-white !bg-transparent hover:!text-purple80 monochrome:!bg-transparent monochrome:!text-white monochrome:hover:!bg-grey70',
      !darkBg &&
        '!text-purple80 monochrome:!text-grey90 !bg-transparent monochrome:!bg-transparent'
    ),
    option: 'cursor-pointer dropdown-label',
    errorText: clsx(
      '-bottom-20px absolute left-0 text-p3',
      !darkBg && 'text-red60',
      darkBg && 'text-white'
    ),
    disabledOption:
      '!text-functional01 monochrome:text-functional01 !bg-transparent hover:!bg-transparent z-50 !cursor-default',
  };

  const {
    optionWrapper,
    option,
    labelStyle,
    arrow,
    errorText,
    headlineStyle,
    headlineWrapper,
    dropdownWrapper,
    disabledOption,
  } = className;

  const disableDefaultStyles = {
    control: () => ({}),
    container: () => ({}),
    option: () => ({}),
    dropdownIndicator: () => ({}),
    clearIndicator: () => ({}),
    group: () => ({}),
    groupHeading: () => ({}),
    indicatorsContainer: () => ({}),
    indicatorSeparator: () => ({}),
    menu: () => ({}),
    menuList: () => ({}),
    menuPortal: () => ({}),
    multiValue: () => ({}),
    multiValueLabel: () => ({}),
    multiValueRemove: () => ({}),
    placeholder: () => ({}),
    singleValue: () => ({}),
    noOptionsMessage: () => ({}),
  };

  const optionContainer = (props: any) => {
    const { isMulti, label, isSelected, isDisabled } = props;

    return (
      <components.Option
        className={clsx(optionWrapper, isDisabled && disabledOption)}
        {...props}
      >
        {isMulti && (
          <Checkbox label={label} checked={isSelected} darkBg={darkBg} />
        )}
        {!isMulti && (
          <label
            style={{
              color: isAuregoShop(whiteLabel) || tradeInWidget ? 'black' : '',
            }}
            className={clsx(option, isDisabled && '!cursor-default')}
          >
            {label}
          </label>
        )}
      </components.Option>
    );
  };

  const clearIndicator = (props: any) => (
    <components.ClearIndicator {...props}>
      <Close20 aria-hidden="true" />
    </components.ClearIndicator>
  );

  const dropdownIndicator = (props: any) => (
    <components.DropdownIndicator {...props}>
      <ChevronDown20 className={arrow} aria-hidden="true" />
    </components.DropdownIndicator>
  );

  return (
    <div
      className={dropdownWrapper}
      data-tracking-id={`${id}--dropdownWrapper`}
    >
      {headline && (
        <div className={headlineWrapper}>
          <div className={headlineStyle}>{headline}</div>
          {hasInfoIcon && <InfoIconBox text={infoText} darkBg={darkBg} />}
        </div>
      )}
      <div className="flex-1 relative">
        <div className="relative focus:z-50" tabIndex={0}>
          {hasMounted ? (
            <>
              <Select
                id={`${id}--input-wrapper`}
                instanceId={`${id}--input`}
                inputId={`${id}--input`}
                ref={overrideSelect}
                components={{
                  MultiValue: multiValueElement,
                  Option: optionContainer,
                  DropdownIndicator: !hideDropdownIndicator
                    ? dropdownIndicator
                    : null,
                  IndicatorSeparator: () => null,
                  ClearIndicator: clearIndicator,
                }}
                className={clsx(
                  'react-select-container',
                  'ausstattung-color',
                  customClassName,
                  darkBg && 'has-dark-bg',
                  hasGreyBg && 'has-grey-bg',
                  fieldRequired &&
                    !isOpen &&
                    !isOptionSelected &&
                    'is-required',
                  context
                )}
                classNamePrefix="react-select"
                onChange={(value) => {
                  onChange?.(value);
                  setIsOptionSelected(!(value == null));
                }}
                isClearable={isClearable && !isMulti}
                options={sortedOptions ?? []}
                isDisabled={disabled}
                isMulti={isMulti}
                isSearchable={isSearchable}
                onMenuOpen={() => setIsOpen(true)}
                onMenuClose={() => setIsOpen(false)}
                placeholder={placeholder}
                closeMenuOnSelect={!isMulti}
                value={value}
                // Added because of bug in react select lib JIRA-Nr:2054
                blurInputOnSelect={isIdentificationForm}
                hideSelectedOptions={false}
                noOptionsMessage={() =>
                  !hideNoOptions
                    ? translate(intl, 'ui.DROPDOWN.NO_OPTIONS')
                    : null
                }
                onInputChange={onInputChange}
                inputValue={inputValue}
                styles={disableDefaultStyles}
                data-custom={customDataString}
              />
              {isLoading && (
                <LoaderOverlay
                  customClassName={{
                    containerCustomClass: 'absolute top-0',
                    loaderCustomClass:
                      '!top-auto !left-auto h-full w-full flex justify-center items-center',
                  }}
                  circularLoaderSize={CircularLoaderSize.sm}
                />
              )}
            </>
          ) : (
            SearchDropdownSkeleton(disabled, placeholder)
          )}
          {label && (
            <label htmlFor={id} className={labelStyle}>
              {label}
            </label>
          )}
        </div>
        {fieldRequired && !isOptionSelected && !isOpen && (
          <div className={errorText} data-tracking-id={`${id}--error`}>
            {errorMessage || translate(intl, 'ui.DROPDOWN.REQUIRED_FIELD')}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchDropdown;
